import {
  StandingsMetricData,
  StandingsMetricDataWithScores,
} from "@app/analytics/MeetingReport/types";
import {
  SAMPLE_MEETING_IDS,
  SAMPLE_METRICS,
} from "@app/analytics/sampleData/sampleMeetings";
import { TOOLTIPS } from "@app/common/content";

type StandingsMetric =
  | "participantScore"
  | "sentiment"
  | "engagement"
  | "charisma"
  | "bias"
  | "microphoneOff"
  | "cameraOff";

export const STANDINGS_METRICS_ITEM: {
  [key in StandingsMetric]: StandingsMetricData;
} = {
  microphoneOff: {
    id: "microphoneOffPercent",
    title: "Microphone Off",
    tooltip: TOOLTIPS.meetingReport.micOff,
    type: "percentage",
    minValue: 0,
  },
  cameraOff: {
    id: "cameraOffPercent",
    title: "Camera Off",
    tooltip: TOOLTIPS.meetingReport.cameraOff,
    type: "percentage",
    minValue: 0,
  },
  participantScore: {
    id: "meetingScore",
    title: "Participant Score",
    tooltip: TOOLTIPS.meetingReport.participantScore,
    minValue: 50,
  },
  sentiment: {
    id: "participantSentiment",
    title: "Sentiment",
    tooltip: TOOLTIPS.meetingReport.sentiment,
    minValue: 50,
  },
  engagement: {
    id: "participantEngagement",
    title: "Engagement",
    tooltip: TOOLTIPS.meetingReport.engagement,
    minValue: 50,
  },
  charisma: {
    id: "participantCharisma",
    title: "Charisma",
    tooltip: TOOLTIPS.meetingReport.charisma,
    minValue: 50,
  },
  bias: {
    id: "participantBias",
    title: "Bias",
    tooltip: TOOLTIPS.meetingReport.bias,
    minValue: 50,
  },
};

const getMetricsFromResults = (rawData: any) => {
  if (!rawData) {
    return { meetingScore: null, sentiment: null, engagement: null };
  }

  const d = rawData.results[0]?.data?.[0];
  const meetingScore = d?.participantMeetingScore?.value;
  const sentiment = d?.participantSentiment?.value;
  const engagement = d?.participantEngagement?.value;
  const charisma = d?.participantCharisma?.value;
  const bias = d?.participantBias?.value;
  const cameraOff = d?.cameraOffPercent?.value;
  const micOff = d?.microphoneOffPercent?.value;

  return {
    meetingScore,
    sentiment,
    engagement,
    charisma,
    bias,
    cameraOff,
    micOff,
  };
};

const getStandingsMetrics = (
  sessionId: string,
  metrics: {
    myMetrics: any;
    everyoneMetrics: any;
  }
): StandingsMetricDataWithScores[] => {
  if (SAMPLE_MEETING_IDS.includes(sessionId)) {
    return SAMPLE_METRICS[sessionId].metrics;
  }

  const {
    meetingScore: myMeetingScore,
    sentiment: mySentiment,
    engagement: myEngagement,
    micOff: myMicOff,
    cameraOff: myCameraOff,
  } = getMetricsFromResults(metrics.myMetrics);

  const {
    meetingScore: everyoneMeetingScore,
    sentiment: everyoneSentiment,
    engagement: everyoneEngagement,
    charisma: myCharisma,
    bias: myBias,
    micOff: everyoneMicOff,
    cameraOff: everyoneCameraOff,
  } = getMetricsFromResults(metrics.everyoneMetrics);

  return [
    {
      ...STANDINGS_METRICS_ITEM.participantScore,
      score: myMeetingScore,
      average: everyoneMeetingScore,
    },
    {
      ...STANDINGS_METRICS_ITEM.engagement,
      score: myEngagement,
      average: everyoneEngagement,
    },
    {
      ...STANDINGS_METRICS_ITEM.sentiment,
      score: mySentiment,
      average: everyoneSentiment,
    },
    {
      ...STANDINGS_METRICS_ITEM.charisma,
      score: myCharisma,
      average: null, // not showing the average of other people's charisma
    },
    {
      ...STANDINGS_METRICS_ITEM.bias,
      score: myBias,
      average: null, // other people can't have bias for themselves
    },
    {
      ...STANDINGS_METRICS_ITEM.microphoneOff,
      score: myMicOff,
      average: everyoneMicOff,
    },
    {
      ...STANDINGS_METRICS_ITEM.cameraOff,
      score: myCameraOff,
      average: everyoneCameraOff,
    },
  ];
};

export default getStandingsMetrics;
